import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'react-redux';
import { DiagnosticSeverity } from 'vscode-languageserver-protocol';
import { DUPLICATE_DRIVE_BASE_MESSAGE_DEFAULT, mergeDuplicateDriveBaseDiagnostics } from '../../Components/CodeEditor/utils/driveBaseSingleton';
import { projectSlice } from '../../redux/ProjectSlice';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import type { RootState } from '../../redux/Store';
import { ensureProgramLspClientDisposedOnUnload, programLspClient } from './programLspClient';

const LINTER_DIAGNOSTICS_TIMEOUT_MS = 5000;

function withLocalDiagnostics(
  code: string,
  diagnostics: Parameters<typeof mergeDuplicateDriveBaseDiagnostics>[1],
  duplicateDriveBaseMessage: string
) {
  return mergeDuplicateDriveBaseDiagnostics(code, diagnostics, duplicateDriveBaseMessage);
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

function createPyrightServiceErrorDiagnostic(errorMessage: string, detail: string) {
  return {
    message: `${errorMessage}\n    ${detail}`,
    severity: DiagnosticSeverity.Error,
    range: {
      start: { line: 0, character: 0 },
      end: { line: 0, character: 0 },
    },
  };
}

export function useProgramDiagnosticsSync(): void {
  const project = useAppSelector((state) => state.project.loadedProject);
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { t } = useTranslation();
  const duplicateDriveBaseMessage = t('editor.duplicateDriveBaseDiagnostic.message', DUPLICATE_DRIVE_BASE_MESSAGE_DEFAULT);
  const duplicateDriveBaseMessageRef = useRef(duplicateDriveBaseMessage);
  const latestCodeRef = useRef('');
  const diagnosticsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const diagnosticsRequestIdRef = useRef(0);

  const clearDiagnosticsTimeout = useCallback(() => {
    if (diagnosticsTimeoutRef.current === null) return;
    clearTimeout(diagnosticsTimeoutRef.current);
    diagnosticsTimeoutRef.current = null;
  }, []);

  useEffect(() => {
    duplicateDriveBaseMessageRef.current = duplicateDriveBaseMessage;
  }, [duplicateDriveBaseMessage]);

  useEffect(() => {
    latestCodeRef.current = project?.program.code ?? '';
  }, [project?.program.code]);

  useEffect(() => {
    ensureProgramLspClientDisposedOnUnload();
  }, []);

  useEffect(() => {
    programLspClient.requestNotification({
      onDiagnostics: ({ diagnostics, programCode }) => {
        const currentCode = store.getState().project.loadedProject?.program.code ?? latestCodeRef.current;
        if (programCode !== currentCode) return;

        clearDiagnosticsTimeout();
        const nextDiagnostics = withLocalDiagnostics(programCode, diagnostics, duplicateDriveBaseMessageRef.current);
        dispatch(projectSlice.actions.setDiagnostics({ diagnostics: nextDiagnostics, programCode }));
      },
      onError: (message: string) => {
        const programCode = store.getState().project.loadedProject?.program.code ?? latestCodeRef.current;
        if (!programCode) return;

        clearDiagnosticsTimeout();
        const errorMessage = t('editor.error.pyright.web.service.error', 'An error occurred when attempting to contact the pyright web service');
        const errorDiagnostic = createPyrightServiceErrorDiagnostic(errorMessage, message);
        dispatch(projectSlice.actions.setDiagnostics({ diagnostics: [errorDiagnostic], programCode }));
      },
      onWaitingForInitialization: (isWaiting) => {
        dispatch(projectSlice.actions.setLinterDiagnosticsPending(isWaiting));
      },
    });
  }, [clearDiagnosticsTimeout, dispatch, duplicateDriveBaseMessage, project?.program.code, store, t]);

  useEffect(
    () => () => {
      clearDiagnosticsTimeout();
    },
    [clearDiagnosticsTimeout]
  );

  useEffect(() => {
    const code = project?.program.code ?? '';
    if (!project) return;

    programLspClient.updateCode(code);
    diagnosticsRequestIdRef.current += 1;
    clearDiagnosticsTimeout();

    if (code === '') {
      dispatch(projectSlice.actions.setDiagnostics({ diagnostics: [], programCode: code }));
      return;
    }

    const requestId = diagnosticsRequestIdRef.current;
    dispatch(projectSlice.actions.setLinterDiagnosticsPending(true));
    diagnosticsTimeoutRef.current = setTimeout(() => {
      if (diagnosticsRequestIdRef.current !== requestId) return;
      diagnosticsTimeoutRef.current = null;

      const currentCode = store.getState().project.loadedProject?.program.code ?? latestCodeRef.current;
      if (code !== currentCode) return;

      const errorMessage = t('editor.error.pyright.web.service.error', 'An error occurred when attempting to contact the pyright web service');
      const timeoutMessage = t('editor.error.pyright.web.service.timeout', 'Timed out waiting for pyright diagnostics');
      const errorDiagnostic = createPyrightServiceErrorDiagnostic(errorMessage, timeoutMessage);
      dispatch(projectSlice.actions.setDiagnostics({ diagnostics: [errorDiagnostic], programCode: code }));
    }, LINTER_DIAGNOSTICS_TIMEOUT_MS);

    void Promise.resolve(programLspClient.updateTextDocument(code)).catch((error: unknown) => {
      const currentCode = store.getState().project.loadedProject?.program.code ?? latestCodeRef.current;
      if (code !== currentCode) return;

      clearDiagnosticsTimeout();
      const errorMessage = t('editor.error.pyright.web.service.error', 'An error occurred when attempting to contact the pyright web service');
      const errorDiagnostic = createPyrightServiceErrorDiagnostic(errorMessage, getErrorMessage(error));
      dispatch(projectSlice.actions.setDiagnostics({ diagnostics: [errorDiagnostic], programCode: code }));
    });
  }, [clearDiagnosticsTimeout, dispatch, project?.id, project?.program.code, project, store, t]);
}
